import React, { useState, useEffect } from 'react';
import { MapPin, X } from 'lucide-react';
import { mockSuggestions } from '../../data/products';
import '../../styles/Auth.css';
import MapPickerModal from '../MapPickerModal';

const AddAddressModal = ({ isOpen, onClose, onSave, editAddress }) => {
  const [formData, setFormData] = useState({
    label: '',
    recipient_name: '',
    landmark: '',
    address: '',
    city: 'Gurgaon',
    state: 'Haryana',
    country: 'India',
    pincode: '',
    phone: '',
    latitude: '',
    longitude: ''
  });

  const [errors, setErrors] = useState({});
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);

  // Update formData when editAddress changes
  useEffect(() => {
    if (editAddress) {
      setFormData({
        label: editAddress.label || '',
        recipient_name: editAddress.recipient_name || '',
        landmark: editAddress.landmark || '',
        address: editAddress.address || '',
        city: editAddress.city || 'Gurgaon',
        state: editAddress.state || 'Haryana',
        country: editAddress.country || 'India',
        pincode: editAddress.pincode != null ? String(editAddress.pincode) : '',
        phone: editAddress.phone != null ? String(editAddress.phone) : '',
        latitude: editAddress.latitude || '',
        longitude: editAddress.longitude || '',
      });
    } else {
      setFormData({
        label: '',
        recipient_name: '',
        landmark: '',
        address: '',
        city: 'Gurgaon',
        state: 'Haryana',
        country: 'India',
        pincode: '',
        phone: '',
        latitude: '',
        longitude: ''
      });
    }
    setErrors({});
  }, [editAddress]);


  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.label.trim()) newErrors.label = 'Label is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode || formData.pincode.length !== 6) {
      newErrors.pincode = 'Valid 6-digit pincode required';
    }
    if (!formData.phone || formData.phone.length !== 10) {
      newErrors.phone = 'Valid 10-digit phone required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleMapConfirm = (data) => {
    setFormData(prev => ({
      ...prev,
      address: data.address || prev.address,
      city: data.city || prev.city,
      state: data.state || prev.state,
      pincode: data.pincode || prev.pincode,
      country: data.country || prev.country,
      latitude: data.latitude ?? prev.latitude,
      longitude: data.longitude ?? prev.longitude,
    }));
    setShowMapPicker(false);
  };

  const searchAddress = (query) => {
    setFormData({ ...formData, address: query });
    if (query.length < 3) {
      setShowAddressSuggestions(false);
      return;
    }
    setAddressSuggestions(mockSuggestions(query));
    setShowAddressSuggestions(false);
  };

  const selectSuggestion = (suggestion) => {
    setFormData({
      ...formData,
      address: suggestion.address,
      city: suggestion.city,
      pincode: suggestion.pincode
    });
    setShowAddressSuggestions(false);
  };

  return (
    <>
    <div className="signup-container">
      <div className="signup-card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <MapPin className="text-blue-600" />
            {editAddress ? 'Edit Address' : 'Add New Address'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Label */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Address Label
            </label>
            <div className="flex gap-2 mb-2">
              {['Home', 'Work', 'Other'].map(preset => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => { setFormData({ ...formData, label: preset }); if (errors.label) setErrors({ ...errors, label: '' }); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border-2 transition-all ${formData.label === preset ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400'}`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <input
              type="text"
              name="label"
              value={formData.label}
              onChange={handleChange}
              placeholder="Or type a custom label"
              className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                errors.label ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.label && <p className="text-red-600 text-xs mt-1">{errors.label}</p>}
          </div>

            {/* Recipient Name */}
            <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Recipient Name
            </label>
            <input
              type="text"
              name="recipient_name" 
                value={formData.recipient_name}
                onChange={handleChange}
                placeholder="Name of the recipient"
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 transition-all"
            />
          </div>

          {/* Address with Autocomplete */}
          <div className="relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Complete Address
            </label>
            <div className="relative">
              <textarea
                name="address"
                rows={3}
                value={formData.address}
                onChange={(e) => searchAddress(e.target.value)}
                onFocus={() => {
                  if ((formData.address || '').length >= 3) {
                    setShowAddressSuggestions(false);
                  }
                }}
                placeholder="Start typing your address..."
                className={`w-full border-2 rounded-xl px-4 py-2.5 sm:py-3 outline-none transition-all resize-none ${
                  errors.address ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
            </div>
            {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}

            <button
              type="button"
              onClick={() => setShowMapPicker(true)}
              className="mt-2 flex items-center gap-2 text-sm text-blue-600 font-medium hover:text-blue-700"
            >
              <MapPin size={15} /> Pick from Google Map
            </button>

            {/* Address Suggestions Dropdown */}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-20 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto hide-scrollbar">
                <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <p className="text-xs text-gray-600 font-medium">
                    {addressSuggestions.length} addresses found
                  </p>
                </div>
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => selectSuggestion(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-all flex items-start gap-3 border-b border-gray-100 last:border-b-0"
                  >
                    <MapPin size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-800 font-medium">
                        {suggestion.address}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Pincode: {suggestion.pincode}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
         {/* landmark */}
         <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Landmark
            </label>
            <input
              type="text"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              placeholder="Nearby landmark"
              className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                errors.landmark ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
              }`}
            />
            {errors.landmark && <p className="text-red-600 text-xs mt-1">{errors.landmark}</p>}
          </div>

          {/* City & State */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="City"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.city ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.city && <p className="text-red-600 text-xs mt-1">{errors.city}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                State
              </label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                placeholder="State"
                className="w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 outline-none focus:border-blue-500 transition-all"
              />
            </div>
          </div>
          
          {/* Pincode & Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode}
                onChange={(e) => {
                  if (/^\d{0,6}$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="6-digit pincode"
                maxLength="6"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.pincode ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.pincode && <p className="text-red-600 text-xs mt-1">{errors.pincode}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Phone
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  if (/^\d{0,10}$/.test(e.target.value)) {
                    handleChange(e);
                  }
                }}
                placeholder="10-digit phone number"
                maxLength="10"
                className={`w-full border-2 rounded-xl px-3 py-2.5 outline-none transition-all ${
                  errors.phone ? 'border-red-300' : 'border-gray-200 focus:border-blue-500'
                }`}
              />
              {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-all"
            >
              {editAddress ? 'Update' : 'Add'} Address
            </button>
          </div>
        </form>
      </div>
    </div>

    {showMapPicker && (
      <MapPickerModal
        onClose={() => setShowMapPicker(false)}
        onConfirm={handleMapConfirm}
        initialLat={formData.latitude || null}
        initialLng={formData.longitude || null}
      />
    )}
    </>
  );
};

export default AddAddressModal;